import React from 'react'
import { connect } from 'react-redux'
import styles from './RegionListItems.scss'
import { showGallery } from 'features/gallery/showGallery'
import { withCollapseState } from 'common/layouts/StandardLayout/StandardLayoutContexts'
import { RichListItem, RichListItemDetail as Detail } from 'common/styleguide/RichListItem'
import { TripThumb, RouteThumb } from 'common/ListItems'
import { HighlightedPhoto } from 'common/ListItems/HighlightedPhoto'
import { Distance, Elevation } from 'common/units'
import { Icon } from 'common/Icon'
import { Postcard } from 'common/Postcard'
import { UserCard } from 'common/hovercards/UserCard'
import { useFormatMessage, commonMessages, commonMetrics, commonTRouteMessages } from 'lib/localization'
import { j } from 'lib/textUtils'
import { urls } from 'lib/urls'
import { urlWithPrivacyCode } from 'lib/utils'
import { routeSelectors } from 'data/resources/routes'
import { postcardSelectors } from 'data/resources/postcards'
import _ from 'lodash'
import { Link } from 'react-router-dom'
import cx from 'classnames'

const LinkOrA = ({ to, href, children }) => to
  ? <Link to={to} className={styles.liLink}>{children}</Link>
  : <a href={href} className={styles.liLink}>{children}</a>

const MainColListItem = withCollapseState(({ objectType, object, details, thumb, description, linkProp, right, collapseState }) => {
  const highlighted = <HighlightedPhoto
    style={collapseState ? { height: '150px', width: '150px' } : { height: '180px', width: '180px' }}
    photoId={object.highlightedPhotoId}
    parentType={objectType}
    parentId={object.id}
    privacyCode={object.privacyCode}
    readonly
  />
  return (
    <>
      {collapseState && objectType !== 'ambassadorRoute' && <>
        {highlighted}
        {thumb}
      </>}
      <RichListItem
        noPadding
        left={(!collapseState && objectType !== 'ambassadorRoute') && highlighted}
        className={styles.regionListItem}
        detailsClassName={collapseState ? styles.collapsedMCLIDetails : undefined}
      >
        <div className={styles.inner}>
          <div className={cx(styles.topRow, collapseState && styles.collapsed)}>
            <div className={styles.details}>
              <h3>
                <LinkOrA {...linkProp}>
                  {object.name}
                </LinkOrA>
              </h3>
              {details}
            </div>
            {!collapseState && thumb}
            {right}
          </div>
          <div className={cx(styles.mcliDescription, collapseState && styles.collapsed)}>
            {description ?? object.description}
          </div>
        </div>
      </RichListItem>
    </>
  )
})

const _AmbassadorRouteListItem = ({ ambassadorRoute, route, postcard, collapseState }) => {
  const fm = useFormatMessage()
  React.useEffect(() => {
    // rwgps.postcards.Postcard.init fires before first paint, so its
    // $container doesn't have the appropriate width. address this by
    // firing off a resize event to trigger Postcard onResize
    window.dispatchEvent(new Event('resize')) // eslint-disable-line
  }, [])

  const ambLogo = <img alt='' height='80' width='80' className={collapseState ? styles.ambassadorLogoCollapsed : styles.ambassadorLogo} src={ambassadorRoute.ambassadorLogo} />
  const postcardClick = (activePhotoId) => showGallery({ photos: postcard.layout.photoIds.map(pid => ({ id: pid })), activePhotoId, readonly: true })

  return (
    <div className={cx(styles.ambassadorRouteListItem, collapseState && styles.collapsed)}>
      {collapseState
        ? <>
          {postcard.layout.photoIds.slice(0, 2).map(pid => <img
            key={pid}
            alt=''
            height='150'
            width='150'
            className={styles.collapsedPostcard}
            src={urls.photos.profile(pid)}
            onClick={() => postcardClick(pid)}
          />)}
        </>
        : <Postcard
          layout={postcard.layout}
          imagecellStyles={styles.imagecells}
          clickableImages
          onImageClick={postcardClick}
        />}

      {!collapseState && ambLogo}

      <MainColListItem
        objectType='ambassadorRoute'
        object={route}
        linkProp={{ href: `/ambassador_routes/${ambassadorRoute.id}` }}
        details={<>
          <div className={collapseState ? styles.detailsWrap : undefined}>
            <Detail data-tip title={fm(commonTRouteMessages.startLocation)}>{j([route.locality, route.administrativeArea])}</Detail>
            <Detail data-tip title={fm(commonMetrics.distance)}><Distance short value={route.distance} /></Detail>
            <Detail data-tip title={_.startCase(fm(commonMetrics.elevationGain))}><Elevation short value={route.elevationGain} /></Detail>
            {route.hasCoursePoints && <Detail data-tip title={fm(commonTRouteMessages.hasCuesheet)}><Icon i='md-document' /></Detail>}
          </div>
          <Detail hoverable>
            <UserCard id={route.userId} name={route.bylineName}>
              <a href={urls.users.show(route.userId)}>
                {fm(commonMessages.byline, { author: route.bylineName })}
              </a>
            </UserCard>
          </Detail>
        </>}
        description={route.description}
        right={collapseState ? ambLogo : undefined}
      />
    </div>
  )
}

export const AmbassadorRouteListItem = connect(
  (state, ownProps) => ({
    route: routeSelectors.find(state, ownProps.ambassadorRoute.routeId),
    postcard: postcardSelectors.byParent(state, 'AmbassadorRoute', ownProps.ambassadorRoute.id)
  }),
  null
)(withCollapseState(_AmbassadorRouteListItem))

export const RouteListItem = withCollapseState(({ route, collapseState }) => {
  const fm = useFormatMessage()

  return (
    <MainColListItem
      objectType='route'
      object={route}
      thumb={<RouteThumb route={route} size={collapseState ? 150 : undefined} />}
      linkProp={{ to: `/routes/${route.id}` }}
      details={<>
        <div className={styles.locationMetricsWrapper}>
          <Detail data-tip title={fm(commonTRouteMessages.startLocation)} className={styles.location}>{j([route.locality, route.administrativeArea])}</Detail>
          <div>
            <Detail data-tip title={fm(commonMetrics.distance)}><Distance short value={route.distance} /></Detail>
            <Detail data-tip title={_.startCase(fm(commonMetrics.elevationGain))}><Elevation short value={route.elevationGain} /></Detail>
            {route.hasCoursePoints && <Detail data-tip title={fm(commonTRouteMessages.hasCuesheet)}><Icon i='md-document' /></Detail>}
          </div>
        </div>
        <div>
          <Detail hoverable>
            <UserCard id={route.userId} name={route.bylineName}>
              <a href={route.bylinePath}>
                {fm(commonMessages.byline, { author: route.bylineName })}
              </a>
            </UserCard>
          </Detail>
        </div>
      </>}
    />
  )
})

export const TripListItem = withCollapseState(({ trip, collapseState }) => {
  const fm = useFormatMessage()

  return (
    <MainColListItem
      objectType='trip'
      object={trip}
      thumb={<TripThumb trip={trip} size={collapseState ? 150 : undefined} />}
      linkProp={{ to: `/trips/${trip.id}` }}
      details={<>
        <div className={styles.locationMetricsWrapper}>
          <Detail data-tip title={fm(commonTRouteMessages.startLocation)} className={styles.location}>{j([trip.locality, trip.administrativeArea])}</Detail>
          <div>
            <Detail data-tip title={fm(commonMetrics.distance)}><Distance short value={trip.distance} /></Detail>
            <Detail data-tip title={_.startCase(fm(commonMetrics.elevationGain))}><Elevation short value={trip.elevationGain} /></Detail>
          </div>
        </div>
        <div>
          <Detail hoverable>
            <UserCard id={trip.userId} name={trip.bylineName}>
              <a href={urls.users.show(trip.userId)}>
                {fm(commonMessages.byline, { author: trip.bylineName })}
              </a>
            </UserCard>
          </Detail>
        </div>
      </>}
    />
  )
})

const SideColListItem = withCollapseState(({ to, href, img, title, details, first, collapseState }) => {
  return <div className={cx(styles.sideColListItem, first && styles.heroItem)}>
    {img && <LinkOrA to={to} href={href}>
      <img alt='' height='70' width='70' src={img} className={cx(first ? styles.bigCover : styles.cover, collapseState && styles.collapsed)} />
    </LinkOrA>}
    <div className={styles.info}>
      <LinkOrA to={to} href={href}>
        <div className={styles.title}>{title}</div>
      </LinkOrA>
      {details}
    </div>
  </div>
})

export const CollectionListItem = ({ collection, first }) => {
  const fm = useFormatMessage()

  return <SideColListItem
    to={urlWithPrivacyCode(`/collections/${collection.id}`, collection.privacyCode)}
    img={urls.isMissing(collection.cover) ? undefined : collection.cover}
    title={collection.title}
    details={<>
      <Distance short value={collection.stats?.distance} />, <Elevation short value={collection.stats?.elevation} />
      <Link to={collection.bylinePath}>{fm(commonMessages.byline, { author: collection.bylineName })}</Link>
    </>}
    first={first}
  />
}

export const RideReportListItem = ({ rideReport }) => {
  const fm = useFormatMessage()

  const { display_name: displayName, first_name: firstName, last_name: lastName, id } = rideReport.user
  const author = displayName ?? `${firstName} ${lastName}`
  return <SideColListItem
    to={`/ride_reports/${rideReport.slug}`}
    img={urls.photos.centi(rideReport.featuredImageId)}
    title={rideReport.title}
    details={<Link to={`/users/${id}`}>{fm(commonMessages.byline, { author })}</Link>}
  />
}

export const ClubListItem = ({ club }) => {
  return <SideColListItem
    href={`/organizations/${club.id}`}
    img={club.logo}
    title={club.name}
    details={<div className={styles.clubSubhead}>
      <span>{club.subhead}</span>
    </div>}
  />
}

export const DirectoryListItem = ({ region, fm, labelString }) => (
  <RichListItem
    title={<Link to={`/regions/${region.zone}/${region.countryCode.toLowerCase()}/${region.slug}`}>{region.name}</Link>}
    left={<img height='100' width='100' className={styles.thumb} src={region.cover} />}
  >
    <div className={styles.directoryListItem}>
      <span className={styles.dliDescription}>
        {region.description}
      </span>
      <span className={styles.routes}>
        {region.routesCount + ' ' + fm(labelString)}
      </span>
    </div>
  </RichListItem>
)

export const SideColRegionListItem = ({ to, name, idx }) => {
  return <Link className={styles.regionRow} to={to}>
    <div className={styles.regionPosition}>
      {`0${idx + 1}`}
    </div>
    <div className={styles.regionName}>
      {name}
    </div>
  </Link>
}
